import React, { useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import { MapContainer, TileLayer, ZoomControl, Marker, AttributionControl, useMap } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import objsService from '../../services/objsService'
import { Layer, Obj } from '../../services/models'
import { Layout, Drawer, Descriptions, Divider, Row, Col, List, Spin, Typography, message } from 'antd'
import { Helmet } from 'react-helmet-async'
import { fetchObjectInfoDirect, ObjectEnrichment } from './enrichment'
import { useDictionaryUi } from './LayerGroupsPanel/useDictionaryUi'
import LayerGroupsPanel from './LayerGroupsPanel/LayerGroupsPanel'
import { GroupFilters, LayerGroup, layerOfGroup, pickGroupColor } from './LayerGroupsPanel/types'

const { Title, Text, Paragraph, Link } = Typography

type Props = {
  layers: Layer[],
}

type MapPoint = {
  obj: Obj,
  // Группа, из которой пришла точка. Объекты разных слоёв не пересекаются
  // (слой нельзя добавить дважды), поэтому id однозначно определяет группу,
  // а группа — слой. По ней резолвятся подписи атрибутов в Drawer.
  groupKey: string,
  lat: number,
  long: number
}

// Иконки кешируются по цвету: новый объект divIcon заставляет Leaflet
// перерисовывать все маркеры и кластеры заново на каждом рендере.
const dotIcons = new Map<string, L.DivIcon>()

const dotIcon = (color: string) => {
  const cached = dotIcons.get(color)
  if (cached) return cached
  const icon = L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,0.5)"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7]
  })
  dotIcons.set(color, icon)
  return icon
}

type ClusterFactory = NonNullable<L.MarkerClusterGroupOptions['iconCreateFunction']>

const clusterFactories = new Map<string, ClusterFactory>()

const clusterFactory = (color: string): ClusterFactory => {
  const cached = clusterFactories.get(color)
  if (cached) return cached
  const factory: ClusterFactory = cluster => L.divIcon({
    className: '',
    html: `<div style="width:40px;height:40px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,0.5);color:#fff;font:700 14px/40px sans-serif;text-align:center">${cluster.getChildCount()}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  })
  clusterFactories.set(color, factory)
  return factory
}

// MapContainer применяет center/zoom только при первом рендере, поэтому
// подгонку границ делаем через дочерний компонент с доступом к карте.
const FitBounds: React.FC<{ points: MapPoint[] }> = ({ points }) => {
  const map = useMap()

  useEffect(() => {
    if (points.length === 0) return
    map.fitBounds(points.map(p => [p.lat, p.long] as [number, number]), { padding: [40, 40] })
  }, [map, points])

  return null
}

const DataPage: React.FC<Props> = ({ layers }) => {
  const [groups, setGroups] = useState<LayerGroup[]>([])
  const [selectedPoint, setSelectedPoint] = useState<MapPoint | null>(null)
  const [selectedFull, setSelectedFull] = useState<Obj | null>(null)
  const [objectInfo, setObjectInfo] = useState<ObjectEnrichment | null>(null)
  const [objectInfoLoading, setObjectInfoLoading] = useState(false)

  const dictUi = useDictionaryUi()
  const groupSeq = useRef(0)
  const fullObjCache = useRef<{ [k: number]: Obj }>({})
  const detailReqId = useRef(0)
  const enrichmentCache = useRef<{ [k: string]: ObjectEnrichment }>({})
  const enrichmentReqId = useRef(0)

  const layersById = useMemo(() => {
    const map: { [k: number]: Layer } = {}
    layers.forEach(l => { map[l.id] = l })
    return map
  }, [layers])

  // Точки каждой группы считаются отдельно: на карте своя группа кластеров на
  // слой, чтобы кластеры разных слоёв не смешивались в один.
  const pointsByGroup = useMemo(() => groups.map(group => ({
    group,
    points: group.items
      .map(o => ({ obj: o, groupKey: group.key, lat: parseFloat(o.lat), long: parseFloat(o.long) }))
      // Координаты приходят строками, parseFloat обязателен. parseFloat(null)
      // даёт NaN, поэтому объекты без координат отсеиваются здесь.
      .filter(p => !Number.isNaN(p.lat) && !Number.isNaN(p.long))
  })), [groups])

  const allPoints = useMemo(() => pointsByGroup.flatMap(g => g.points), [pointsByGroup])

  const loadObjDetail = (obj: Obj) => {
    if (fullObjCache.current[obj.id]) {
      setSelectedFull(fullObjCache.current[obj.id])
      return
    }
    const reqId = ++detailReqId.current
    objsService.getById(obj.id)
      .then((response) => {
        if (reqId !== detailReqId.current) return
        fullObjCache.current[obj.id] = response.obj
        setSelectedFull(response.obj)
      })
      .catch(() => {
        if (reqId !== detailReqId.current) return
        setSelectedFull(null)
      })
  }

  const loadEnrichment = (obj: Obj, layer: Layer | null) => {
    const cacheKey = `${layer?.id}:${obj.id}`
    if (enrichmentCache.current[cacheKey]) {
      setObjectInfo(enrichmentCache.current[cacheKey])
      setObjectInfoLoading(false)
      return
    }
    const reqId = ++enrichmentReqId.current
    setObjectInfoLoading(true)
    setObjectInfo(null)
    fetchObjectInfoDirect({ name: obj.name, src: obj.src, category: layer?.name ?? '' })
      .then(info => {
        if (reqId !== enrichmentReqId.current) return
        enrichmentCache.current[cacheKey] = info
        setObjectInfo(info)
        setObjectInfoLoading(false)
      })
      .catch(() => {
        if (reqId !== enrichmentReqId.current) return
        setObjectInfoLoading(false)
      })
  }

  const handlePick = (point: MapPoint) => {
    setSelectedPoint(point)
    loadObjDetail(point.obj)
    loadEnrichment(point.obj, layerOfGroup(groups, layersById, point.groupKey))
  }

  const addGroup = (layerId: number) => {
    setGroups(old => [...old, {
      key: `g${++groupSeq.current}`,
      layerId,
      color: pickGroupColor(old.length),
      filters: {},
      items: [],
      loading: false,
      applied: false
    }])
  }

  const applyGroup = async (groupKey: string, filters: GroupFilters) => {
    const group = groups.find(g => g.key === groupKey)
    if (!group) return
    setGroups(old => old.map(g => g.key === groupKey ? { ...g, loading: true } : g))
    try {
      const response = await objsService.map(group.layerId, filters)
      setGroups(old => old.map(g => g.key === groupKey
        ? { ...g, filters, items: response.objs, loading: false, applied: true }
        : g))
    } catch {
      setGroups(old => old.map(g => g.key === groupKey ? { ...g, loading: false } : g))
      message.error('Не удалось загрузить объекты слоя')
    }
  }

  // Удаление группы закрывает Drawer, если открыт объект именно этой группы:
  // подписи атрибутов резолвятся через слой группы, иначе они бы пропали.
  const removeGroup = (groupKey: string) => {
    setGroups(old => old.filter(g => g.key !== groupKey))
    setSelectedPoint(prev => prev?.groupKey === groupKey ? null : prev)
  }

  const activeLayer = selectedPoint ? layerOfGroup(groups, layersById, selectedPoint.groupKey) : null

  const renderValue = (v: string | number) =>
    typeof v === 'string' && /^https?:\/\//.test(v)
      ? <a href={v} target="_blank" rel="noopener noreferrer">Открыть</a>
      : v

  const drawerObj = selectedFull !== null && selectedPoint !== null && selectedFull.id === selectedPoint.obj.id
    ? selectedFull
    : selectedPoint?.obj ?? null

  return (
    <>
      <Helmet><title>Prisma — Карта данных</title></Helmet>
        <Layout.Content style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <LayerGroupsPanel
            layers={layers}
            groups={groups}
            dictUi={dictUi}
            onAdd={addGroup}
            onApply={applyGroup}
            onRemove={removeGroup}
          />
          <MapContainer center={[55.751574, 37.573856]} zoom={4} zoomControl={false} attributionControl={false} style={{ width: '100%', height: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <ZoomControl position="topright" />
            {/* Свой контрол вместо дефолтного: у Leaflet в атрибуции печатается
                префикс «Leaflet |», а копирайт OpenStreetMap убрать нельзя —
                это требование лицензии тайлов. Поэтому prefix={false}. */}
            <AttributionControl position="bottomright" prefix={false} />
            <FitBounds points={allPoints} />
            {pointsByGroup.map(({ group, points }) => (
              <MarkerClusterGroup key={group.key} chunkedLoading iconCreateFunction={clusterFactory(group.color)}>
                {points.map(p => (
                  <Marker
                    key={p.obj.id}
                    position={[p.lat, p.long]}
                    icon={dotIcon(group.color)}
                    eventHandlers={{ click: () => handlePick(p) }}
                  />
                ))}
              </MarkerClusterGroup>
            ))}
          </MapContainer>
          </div>
        </Layout.Content>
      <Drawer
        title={selectedPoint?.obj.name}
        width={750}
        onClose={() => setSelectedPoint(null)}
        open={!!selectedPoint}
      >
        {drawerObj && (
          <Row gutter={24}>
            <Col span={12}>
              <Title level={5}>Данные объекта</Title>
              <Descriptions column={1} bordered size="small" layout="horizontal">
                <Descriptions.Item label="Название">{drawerObj.name}</Descriptions.Item>
                {!!drawerObj.description && (
                  <Descriptions.Item label="Описание">{drawerObj.description}</Descriptions.Item>
                )}
                {!!drawerObj.src && (
                  <Descriptions.Item label="Источник">{renderValue(drawerObj.src)}</Descriptions.Item>
                )}
                <Descriptions.Item label="Координаты">{drawerObj.lat}, {drawerObj.long}</Descriptions.Item>
                {(drawerObj.numberValues ?? []).filter(nv => nv.value !== null && nv.value !== undefined).map(nv => {
                  const attr = activeLayer?.attributeNumbers?.find(a => a.id === nv.attributeNumberId)
                  return (
                    <Descriptions.Item key={`n:${nv.attributeNumberId}`}
                                       label={attr ? (attr.unit ? `${attr.name} (${attr.unit})` : attr.name) : `ID: ${nv.attributeNumberId}`}>
                      {nv.value}
                    </Descriptions.Item>
                  )
                })}
                {(drawerObj.textValues ?? []).filter(tv => tv.value !== null && tv.value !== undefined).map(tv => {
                  const attr = activeLayer?.attributeTexts?.find(a => a.id === tv.attributeTextId)
                  return (
                    <Descriptions.Item key={`t:${tv.attributeTextId}`}
                                       label={attr?.name ?? `ID: ${tv.attributeTextId}`}>
                      {renderValue(tv.value)}
                    </Descriptions.Item>
                  )
                })}
                {(drawerObj.dictionaryValues ?? []).map(dv => {
                  const attr = activeLayer?.attributeDictionaries?.find(a => a.id === dv.attributeDictionaryId)
                  const val = attr?.values?.find(v => v.id === dv.valueId)
                  return (
                    <Descriptions.Item key={`d:${dv.attributeDictionaryId}`}
                                       label={attr?.name ?? `ID: ${dv.attributeDictionaryId}`}>
                      {val?.name ?? `#${dv.valueId}`}
                    </Descriptions.Item>
                  )
                })}
              </Descriptions>
            </Col>
            <Col span={12}>
              <Title level={5}>Публичные данные</Title>
              {objectInfoLoading
                ? <Spin tip="Сбор данных..." />
                : (
                <div>
                  {objectInfo?.specs && (
                    <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                      {Object.entries(objectInfo.specs).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text strong>{key}:</Text> <Text>{value}</Text>
                        </div>
                      ))}
                    </div>
                  )}
                  {objectInfo?.wiki
                    ? (
                    <div>
                      {objectInfo.wiki.thumbnail?.source &&
                        <img src={objectInfo.wiki.thumbnail.source} alt="thumbnail" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: 12 }} />}
                      <Paragraph ellipsis={{ rows: 8, expandable: true, symbol: 'читать далее' }}>{objectInfo.wiki.extract}</Paragraph>
                      {objectInfo.wiki.content_urls?.desktop?.page &&
                        <Link href={objectInfo.wiki.content_urls.desktop.page} target="_blank">Открыть источник (Wikipedia)</Link>}
                    </div>
                      )
                    : objectInfo?.src
                      ? (
                    <div style={{ background: '#fff7e6', border: '1px solid #fa8c16', borderRadius: 8, padding: '8px 12px' }}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>Данные с сайта-источника (SRC):</Text>
                      <Paragraph style={{ display: 'block', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}>{objectInfo.src}</Paragraph>
                      {drawerObj.src &&
                        <Link href={drawerObj.src} target="_blank" style={{ display: 'block', marginTop: 4 }}>Открыть первоисточник</Link>}
                    </div>
                        )
                      : (
                          !objectInfo?.specs && <Text type="secondary">Информация в открытых источниках не найдена.</Text>
                        )}
                </div>
                  )}
            </Col>
            <Col span={24} style={{ marginTop: 24 }}>
              <Divider>Новости по объекту (до 10 последних)</Divider>
              {objectInfoLoading
                ? <Spin tip="Поиск новостей..." />
                : (
                    objectInfo?.news && objectInfo.news.length > 0
                      ? (
                  <List itemLayout="vertical" size="small" dataSource={objectInfo.news} renderItem={item => (
                    <List.Item key={item.link}>
                      <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>{item.title}</a>
                      <div style={{ fontSize: 12, color: '#888' }}>{item.pubDate}</div>
                    </List.Item>
                  )} />
                        )
                      : (
                  <Text type="secondary">Строгих новостей по данному объекту не найдено.</Text>
                        )
                  )}
            </Col>
          </Row>
        )}
      </Drawer>
    </>
  )
}

export default DataPage
